import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from '@components';
import { PendingLegalDocument } from './legalService';

interface LegalConsentOverlayProps {
  documents: PendingLegalDocument[];
  /** Called when the user accepts all documents (after a successful API call). */
  onAccept: () => Promise<void>;
  /** Called when the user declines. Should log the user out. */
  onDecline: () => void;
}

/**
 * Blocking overlay shown after login when the organization has not yet
 * accepted the currently active legal documents (AGB / AVV).
 *
 * It intentionally cannot be dismissed by clicking the backdrop or pressing
 * Escape: the user must either accept every document or decline (log out).
 */
export const LegalConsentOverlay: React.FC<LegalConsentOverlayProps> = ({
  documents,
  onAccept,
  onDecline,
}) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');

  const allChecked = useMemo(
    () => documents.length > 0 && documents.every(doc => checked[doc.id]),
    [documents, checked]
  );

  const toggle = (id: string) => {
    setChecked(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAccept = async () => {
    if (!allChecked || isSubmitting) return;
    setIsSubmitting(true);
    setError('');
    try {
      await onAccept();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('legal.acceptError'));
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      {/* Non-dismissible backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-60 dark:bg-opacity-75" aria-hidden />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="legal-consent-title"
          className="relative w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-800"
        >
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-gray-200 p-6 dark:border-gray-700">
            <span className="material-icons text-primary-500">gavel</span>
            <h2
              id="legal-consent-title"
              className="text-lg font-semibold text-gray-900 dark:text-white"
            >
              {t('legal.consentTitle')}
            </h2>
          </div>

          {/* Body */}
          <div className="space-y-4 p-6">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {t('legal.consentIntro')}
            </p>

            <ul className="space-y-3">
              {documents.map(doc => (
                <li
                  key={doc.id}
                  className="rounded-md border border-gray-200 p-3 dark:border-gray-700"
                >
                  <div className="flex items-start justify-between gap-3">
                    <Checkbox
                      id={`legal-doc-${doc.id}`}
                      checked={!!checked[doc.id]}
                      onChange={() => toggle(doc.id)}
                      label={t('legal.acceptDocument', {
                        type: doc.type_label,
                        version: doc.version,
                      })}
                    />
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                    >
                      <span className="material-icons text-base leading-none">open_in_new</span>
                      {t('legal.viewDocument')}
                    </a>
                  </div>
                </li>
              ))}
            </ul>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 border-t border-gray-200 p-6 dark:border-gray-700">
            <Button
              variant="ghost"
              onClick={onDecline}
              disabled={isSubmitting}
            >
              {t('legal.decline')}
            </Button>
            <Button
              variant="primary"
              onClick={handleAccept}
              disabled={!allChecked || isSubmitting}
              loading={isSubmitting}
            >
              {t('legal.accept')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
